import { lockedGoldABI } from '@celo/abis';
import { Addresses } from 'src/config/contracts';
import { ParticipationData, scoreParticipation } from 'src/features/delegation/participation';
import { Delegatee, DelegateeMetadata } from 'src/features/delegation/types';
import { fromFixidity } from 'src/utils/numbers';
import { PublicClient } from 'viem';

export interface DelegateesData {
  addressToDelegatee: AddressTo<Delegatee>;
  delegatees: Delegatee[];
}

export async function fetchDelegateeStats(
  publicClient: PublicClient,
  metadata: DelegateeMetadata[],
  // Absent when the DB query failed; every delegate then renders without a score
  participation?: ParticipationData,
): Promise<AddressTo<Delegatee>> {
  const votingPowerResults = await publicClient.multicall({
    contracts: metadata.map(
      (d) =>
        ({
          address: Addresses.LockedGold,
          abi: lockedGoldABI,
          functionName: 'getAccountTotalGovernanceVotingPower',
          args: [d.address],
        }) as const,
    ),
  });

  const delegatedFractionResults = await publicClient.multicall({
    contracts: metadata.map(
      (d) =>
        ({
          address: Addresses.LockedGold,
          abi: lockedGoldABI,
          functionName: 'getAccountTotalDelegatedFraction',
          args: [d.address],
        }) as const,
    ),
  });

  const delegatedToResults = await publicClient.multicall({
    contracts: metadata.map(
      (d) =>
        ({
          address: Addresses.LockedGold,
          abi: lockedGoldABI,
          functionName: 'totalDelegatedCelo',
          args: [d.address],
        }) as const,
    ),
  });

  // Process validator lists to create list of validator groups
  const delegatees: AddressTo<Delegatee> = {};
  for (let i = 0; i < metadata.length; i++) {
    const address = metadata[i].address as Address;

    const votingPowerRes = votingPowerResults[i];
    const delegatedFractionRes = delegatedFractionResults[i];
    const delegatedToRes = delegatedToResults[i];

    if (
      votingPowerRes.status !== 'success' ||
      delegatedFractionRes.status !== 'success' ||
      delegatedToRes.status !== 'success'
    )
      throw new Error('Error fetching delegatee stats');

    const votingPower = votingPowerRes.result as bigint;
    const delegatedByPercent = fromFixidity(delegatedFractionRes.result as bigint) * 100;
    const delegatedToBalance = delegatedToRes.result as bigint;

    delegatees[address] = {
      ...metadata[i],
      address,
      votingPower,
      delegatedToBalance,
      delegatedByPercent,
      participation:
        participation &&
        scoreParticipation(
          participation.votedRanks[address.toLowerCase() as Address] ?? [],
          participation.closedProposals,
        ),
    };
  }

  return delegatees;
}
